/**
 * Version Checker - Detects new app versions and auto-refreshes
 *
 * When a new version is deployed:
 * 1. Detects version mismatch
 * 2. Clears caches
 * 3. Silently refreshes the page
 *
 * No scary error messages - just smooth updates!
 */

interface VersionInfo {
  version: string;
  buildTime: string;
}

export class VersionChecker {
  private static currentVersion: string | null = null;
  private static checkInterval: number = 5 * 60 * 1000; // Check every 5 minutes
  private static intervalId: number | null = null;
  private static isChecking = false;

  /**
   * Initialize version checking
   * Fetches current version and starts periodic checks
   */
  static async initialize(): Promise<void> {
    try {
      // Get initial version
      const versionInfo = await this.fetchVersion();
      this.currentVersion = versionInfo.version;
      console.log('🚀 App version:', this.currentVersion);

      // Start periodic checks
      this.startPeriodicCheck();

      // Check on visibility change (when user returns to tab)
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          this.checkForUpdate();
        }
      });

      // Check on focus (when user clicks on window)
      window.addEventListener('focus', () => {
        this.checkForUpdate();
      });
    } catch (error) {
      console.warn('Version checker initialization failed:', error);
      // Don't throw - version checking is optional
    }
  }

  /**
   * Fetch version info from server
   */
  private static async fetchVersion(): Promise<VersionInfo> {
    const response = await fetch('/version.json?t=' + Date.now(), {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch version');
    }

    return response.json();
  }

  /**
   * Check if a new version is available
   */
  static async checkForUpdate(): Promise<boolean> {
    // Prevent concurrent checks
    if (this.isChecking) {
      return false;
    }

    this.isChecking = true;

    try {
      const versionInfo = await this.fetchVersion();

      // If version changed, trigger update
      if (this.currentVersion && versionInfo.version !== this.currentVersion) {
        console.log('🔄 New version detected:', versionInfo.version, '(was:', this.currentVersion + ')');
        await this.performUpdate();
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Version check failed:', error);
      return false;
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Perform the update - clear caches and reload
   */
  private static async performUpdate(): Promise<void> {
    console.log('🔄 Updating to new version...');

    // Clear service worker caches
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('✅ Caches cleared');
      } catch (error) {
        console.warn('Failed to clear caches:', error);
      }
    }

    // Show brief "Updating..." message (optional)
    this.showUpdateNotification();

    // Force hard reload (bypass cache)
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }

  /**
   * Show a brief update notification (optional)
   */
  private static showUpdateNotification(): void {
    // Create a simple toast notification
    const toast = document.createElement('div');
    toast.textContent = '🔄 Updating to latest version...';
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #1976d2;
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 14px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 999999;
      animation: slideIn 0.3s ease-out;
    `;

    // Add animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(toast);
  }

  /**
   * Start periodic version checking
   */
  private static startPeriodicCheck(): void {
    if (this.intervalId !== null) {
      return; // Already running
    }

    this.intervalId = window.setInterval(() => {
      this.checkForUpdate();
    }, this.checkInterval);
  }

  /**
   * Stop periodic version checking
   */
  static stopPeriodicCheck(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Force an immediate update check
   */
  static forceCheck(): void {
    this.checkForUpdate();
  }
}

// Auto-initialize when imported
if (typeof window !== 'undefined') {
  VersionChecker.initialize().catch(console.warn);
}
